import React, { useState } from 'react';
import { Button, Input, message } from 'antd';

import { apiPHP } from '../../../../services/api';
import { TIPO_JOGO } from '../../../../services/admin';
import { apiErrorMessage, fieldLabel, type GeneratorProps } from './types';

// Formulário de quiz (POST /create_jogo + /create_quiz, uma por questão) do
// "+ Novo jogo". Diferença em relação ao protótipo antigo (já removido): o jogo nasce com `TIPO_JOGO.QUIZ` (6) — o
// protótipo mandava `id_tipo: 2`, e um quiz com tipo 2 não aparece como "Quiz" no
// catálogo nem no menu do app (que usam o 6).

interface Question {
  pergunta: string;
  alternativas: string[];
  correta: number;
}

interface CreateJogoResponse {
  id_jogo: number;
}

const emptyQuestion = (): Question => ({ pergunta: '', alternativas: ['', ''], correta: 0 });

export const QuizForm: React.FC<GeneratorProps> = ({ resolveTitle, onCreated }) => {
  const [questions, setQuestions] = useState<Question[]>([emptyQuestion()]);
  const [loading, setLoading] = useState(false);

  const setQuestion = (i: number, patch: Partial<Question>) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const setAlternative = (qi: number, ai: number, value: string) =>
    setQuestions((qs) =>
      qs.map((q, idx) =>
        idx === qi
          ? { ...q, alternativas: q.alternativas.map((a, j) => (j === ai ? value : a)) }
          : q,
      ),
    );

  const create = async () => {
    const titulo = await resolveTitle();
    if (titulo === null) return;
    const payload = questions
      .map((q) => ({
        pergunta: q.pergunta.trim(),
        alternatives: q.alternativas
          .map((a, i) => ({ alternative: a.trim(), correct: i === q.correta }))
          .filter((a) => a.alternative.length > 0),
      }))
      .filter(
        (q) => q.pergunta && q.alternatives.length >= 2 && q.alternatives.some((a) => a.correct),
      );
    if (payload.length === 0) {
      message.error('Adicione ao menos 1 questão com 2+ alternativas e a correta marcada.');
      return;
    }
    setLoading(true);
    try {
      const jogo = await apiPHP.post<CreateJogoResponse>('/create_jogo', {
        titulo,
        id_tipo: TIPO_JOGO.QUIZ,
      });
      const id = jogo.data.id_jogo;
      let ordem = 1;
      for (const q of payload) {
        // eslint-disable-next-line no-await-in-loop
        await apiPHP.post('/create_quiz', {
          id_jogo: id,
          ordem: ordem++,
          question: q.pergunta,
          alternatives: q.alternatives,
        });
      }
      onCreated(id);
    } catch (e: unknown) {
      message.error(apiErrorMessage(e, 'Falha ao criar o quiz.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {questions.map((q, qi) => (
        <div key={qi} style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 12 }}>
          <label style={fieldLabel} htmlFor={`quiz-question-${qi}`}>
            Pergunta {qi + 1}
          </label>
          <Input
            id={`quiz-question-${qi}`}
            value={q.pergunta}
            onChange={(e) => setQuestion(qi, { pergunta: e.target.value })}
            placeholder="Qual é a capital do Brasil?"
          />
          <div style={{ marginTop: 8 }}>
            {q.alternativas.map((a, ai) => (
              <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <input
                  type="radio"
                  name={`correta-${qi}`}
                  checked={q.correta === ai}
                  onChange={() => setQuestion(qi, { correta: ai })}
                  title="Marcar como correta"
                />
                <Input
                  value={a}
                  onChange={(e) => setAlternative(qi, ai, e.target.value)}
                  placeholder={`Alternativa ${ai + 1}`}
                />
              </div>
            ))}
            <Button
              size="small"
              onClick={() => setQuestion(qi, { alternativas: [...q.alternativas, ''] })}
              style={{ marginTop: 8 }}
            >
              + alternativa
            </Button>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 16 }}>
        <Button onClick={() => setQuestions((qs) => [...qs, emptyQuestion()])}>
          + adicionar questão
        </Button>{' '}
        <Button type="primary" loading={loading} onClick={create}>
          Criar quiz
        </Button>
      </div>
    </div>
  );
};

export default QuizForm;
